import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Loader2, ClipboardCheck, CheckCircle2, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CERTIFICATIONS = [
  "UEFA A License", "UEFA B License", "UEFA C License",
  "AFC A License", "AFC B License", "AFC C License",
  "FAS Grassroots", "FAS Youth", "Other", "None",
];

export default function CoachApplication({ user }) {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [phone, setPhone] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [nroc, setNroc] = useState(false);
  const [certs, setCerts] = useState([]);
  const [coaching, setCoaching] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/me/coach-application`, { withCredentials: true })
      .then((r) => {
        setState(r.data);
        setPhone(r.data.prefill?.phone || "");
        setBirthdate((r.data.prefill?.birthdate || "").slice(0, 10));
      })
      .catch(() => setState({ error: true }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!phone.trim() || !birthdate) {
      toast.error("Mobile number and date of birth are needed");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/me/coach-application`, {
        phone: phone.trim(),
        birthdate,
        nroc_registered: nroc,
        coaching_certifications: certs,
        currently_coaching: coaching.split("\n").map((l) => l.trim()).filter(Boolean),
      }, { withCredentials: true });
      toast.success(r.data.message);
      const fresh = await axios.get(`${API}/me/coach-application`, { withCredentials: true });
      setState(fresh.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit the application");
    } finally {
      setBusy(false);
    }
  };

  if (!state) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const prefill = state.prefill || {};
  const pending = state.status === "pending";
  const done = state.is_coach || state.status === "approved";

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-slate-400 hover:text-white text-sm flex items-center gap-1 mb-6"
          data-testid="coach-apply-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-slate-900 to-yellow-600
                          flex items-center justify-center">
            <ClipboardCheck className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Coach for the Academy</h1>
        </div>
        <p className="text-slate-400 text-sm mb-6">
          You&apos;re signed in, so we already know who you are — just the coaching details below.
          Your existing login becomes your coach login; nothing new to remember.
        </p>

        {done && (
          <Card className="bg-emerald-500/10 border-emerald-500/30 p-5" data-testid="coach-apply-approved">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-emerald-200 font-semibold">You&apos;re set up as a coach</p>
                <p className="text-slate-300 text-sm mt-1">
                  Your registers and sessions appear under Academy once an admin has assigned your
                  teams.
                </p>
                <Link to="/academy">
                  <Button className="mt-3 bg-emerald-600 hover:bg-emerald-500" data-testid="coach-apply-go-academy">
                    Go to Academy
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        )}

        {!done && pending && (
          <Card className="bg-amber-500/10 border-amber-500/30 p-5" data-testid="coach-apply-pending">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-amber-200 font-semibold">Application received</p>
                <p className="text-slate-300 text-sm mt-1">
                  Sent on {(state.application?.created_at || "").slice(0, 10)}. An admin reviews it
                  and your coach access is added to this same login — you won&apos;t need another
                  account.
                </p>
              </div>
            </div>
          </Card>
        )}

        {!done && !pending && (
          <form onSubmit={submit} className="space-y-5">
            <Card className="bg-slate-900/60 border-white/5 p-5 space-y-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Your account</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Name</label>
                  <Input value={prefill.name || ""} disabled className="bg-slate-800/60"
                    data-testid="coach-apply-name" />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Email (your login)</label>
                  <Input value={prefill.email || ""} disabled className="bg-slate-800/60"
                    data-testid="coach-apply-email" />
                </div>
              </div>
              <p className="text-[11px] text-slate-500">
                Taken from your account, so your coach role lands on this login — not a second one.
              </p>
            </Card>

            <Card className="bg-slate-900/60 border-white/5 p-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Mobile number *</label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                    placeholder="+65 9123 4567" data-testid="coach-apply-phone" />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Date of birth *</label>
                  <Input type="date" value={birthdate} onChange={(e) => setBirthdate(e.target.value)}
                    data-testid="coach-apply-birthdate" />
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={nroc} onCheckedChange={(v) => setNroc(!!v)}
                  data-testid="coach-apply-nroc" />
                <span className="text-sm text-slate-300">
                  I&apos;m registered with NROC (National Registry of Coaches)
                </span>
              </label>

              <div>
                <p className="text-xs text-slate-400 mb-2">Coaching qualifications</p>
                <div className="grid sm:grid-cols-2 gap-1.5">
                  {CERTIFICATIONS.map((c) => (
                    <label key={c} className="flex items-center gap-2 cursor-pointer text-sm text-slate-300">
                      <Checkbox
                        checked={certs.includes(c)}
                        onCheckedChange={(v) => setCerts((s) => (v ? [...s, c] : s.filter((x) => x !== c)))}
                        data-testid={`coach-apply-cert-${c.replace(/\s+/g, "-").toLowerCase()}`}
                      />
                      {c}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">
                  Teams you currently coach (one per line)
                </label>
                <Textarea value={coaching} onChange={(e) => setCoaching(e.target.value)}
                  rows={3} placeholder={"SCC U12\nSchool team"} className="bg-slate-800/60"
                  data-testid="coach-apply-current" />
              </div>
            </Card>

            <Button type="submit" disabled={busy}
              className="w-full h-12 bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
              data-testid="coach-apply-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Apply to coach
            </Button>
            <p className="text-xs text-slate-500 text-center">
              An admin reviews applications and assigns your teams. You keep your current access
              either way.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
